import { Box, Stack } from "@mui/material";
import { ConstantContext } from "components/context/ConstantProvider";
import { UserContext } from "components/context/UserProvider";
import { Form, FormContext } from "components/shared/Form";
import { buildGenericModelForm } from "components/shared/GenericModelForm";
import FooterFormDates from "components/ui/atoms/FooterFormDates";
import FormButtons from "components/ui/atoms/FormButtons";
import CompanyField from "components/ui/atoms/formFields/CompanyField";
import DictionaryField from "components/ui/atoms/formFields/DictionaryField";
import RHFCheckbox from "components/ui/atoms/formFields/RHFCheckbox";
import RHFDatePicker from "components/ui/atoms/formFields/RHFDatePicker";
import RHFTextField from "components/ui/atoms/formFields/RHFTextField";
import TagField from "components/ui/atoms/formFields/TagField";
import UserField from "components/ui/atoms/formFields/UserField";
import CompanyViewAsCard from "models/companies/CompanyViewAsCard";
import FinancialsWidget from "models/companies/FinancialsWidget";
import {
  useCreateSourcing,
  useDeleteSourcing,
  useSourcing,
  useUpdateSourcing,
} from "models/sourcings/apiSourcing";
import SourcingFormHeader from "models/sourcings/SourcingFormHeader";
import { useContext } from "react";
import * as yup from "yup";

export const validationSchema = yup.object({
  company: yup
    .number()
    .typeError("Please provide a company")
    .required("Please provide a company")
    .positive()
    .integer(),
  sourcing_owner: yup.string().required("Please provide an owner"),
  deal_flow: yup.number().nullable(true).required("Please provide a dealflow"),
  status: yup.string().required(),
  reminder_date: yup
    .string()
    .nullable(true)
    .when("status", (status) => {
      if (status == "REMINDER") {
        return yup
          .string()
          .nullable(true)
          .required("You must provide a date if status is reminder");
      }
    }),
  reason_to_close: yup.string().nullable(),
  reason_to_close_tag: yup.array().nullable(),
  context: yup.string().nullable(),
  actions_taken: yup.string().nullable(),
  to_be_checked: yup.bool().required(),
});

export const getInitialValues = (object) => {
  return {
    id: object?.id || null,
    company: object?.company || null,
    company_detail: object?.company_detail || null,
    sourcing_owner: object?.sourcing_owner || null,
    sourcing_owner_detail: object?.sourcing_owner_detail || null,
    deal_flow: object?.deal_flow || "",
    status: object?.status || "",
    reminder_date: object?.reminder_date || null,
    context: object?.context || "",
    reason_to_close: object?.reason_to_close || "",
    reason_to_close_tag: (object?.reason_to_close_tag || []).sort(),
    actions_taken: object?.actions_taken || "",
    to_be_checked:
      object?.to_be_checked === undefined ? true : object?.to_be_checked,
  };
};

export const SourcingFormFields = () => {
  const { dealFlows, sourcingStatuses } = useContext(ConstantContext);
  const {
    form: { watch, trigger, setValue },
    object: sourcing,
    onCancel,
    formType,
  } = useContext(FormContext);

  const company = watch("company");
  const status = watch("status");

  return (
    <Box
      sx={{
        position: "relative",
        overflow: "auto",
        width: "inherit",
        maxWidth: "inherit",
      }}
    >
      <SourcingFormHeader
        formType={formType}
        onClose={onCancel}
        sourcing={sourcing}
      />
      {/* the "70px" should be calculated with appBarHeight from context */}
      <Stack spacing={2} p={2} pt="70px">
        {company && <CompanyViewAsCard companyId={company} />}
        <CompanyField name="company" label="Company" creatable />
        <UserField name="sourcing_owner" label="Owner" />
        <RHFCheckbox name="to_be_checked" label="To be checked" />
        <DictionaryField
          optionsDictionary={Object.fromEntries(
            dealFlows.map((x) => [x.id, x.name]),
          )}
          name="deal_flow"
          label="Dealflow"
        />
        <DictionaryField
          optionsDictionary={Object.fromEntries(
            sourcingStatuses.map((x) => [x.code, x.label]),
          )}
          name="status"
          label="Sourcing Status"
          onChange={(value) => {
            trigger("reminder_date");
            if (value !== "REMINDER") {
              setValue("reminder_date", null);
            }
          }}
        />
        {status === "REMINDER" && (
          <RHFDatePicker name="reminder_date" label="Reminder date" fullWidth />
        )}
        <RHFTextField
          name="context"
          label="Context"
          fullWidth
          multiline
          maxRows={20}
        />
        <RHFTextField
          name="actions_taken"
          label="Actions taken"
          fullWidth
          multiline
          maxRows={20}
        />
        <RHFTextField
          name="reason_to_close"
          label="Reason to close"
          fullWidth
          multiline
          maxRows={20}
        />
        <TagField
          name="reason_to_close_tag"
          label="Reason to close tags"
          tagTypeId="TAG_REASON_TO_CLOSE"
          multiple
        />
        {company && <FinancialsWidget companyId={company} />}
        {sourcing && <FooterFormDates object={sourcing} />}
        <Box height={80} />
      </Stack>
      <FormButtons />
    </Box>
  );
};

const SourcingForm = (props) => {
  return (
    <Form
      validationSchema={validationSchema}
      prepareInitialValues={getInitialValues}
      {...props}
    >
      <SourcingFormFields />
    </Form>
  );
};

const BasicModelSourcingForm = buildGenericModelForm({
  form: SourcingForm,
  useGet: useSourcing,
  useCreate: useCreateSourcing,
  useUpdate: useUpdateSourcing,
  useDelete: useDeleteSourcing,
});

export const ModelSourcingForm = (props) => {
  const { refetchCountSourcing } = useContext(UserContext);

  const enhanced = (func) => {
    return (...args) => {
      refetchCountSourcing();
      func && func(...args);
    };
  };

  return (
    <BasicModelSourcingForm
      {...props}
      onUpdated={enhanced(props.onUpdated)}
      onCreated={enhanced(props.onCreated)}
      onDeleted={enhanced(props.onDeleted)}
    />
  );
};
